import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import { paymentAPI, handleAPIError } from '../services/api';
import { 
  CreditCard, 
  DollarSign, 
  Shield, 
  Check, 
  Clock,
  AlertTriangle,
  X,
  Loader2,
  MessageSquare,
  Users,
  Settings,
  Star,
  Zap,
  Crown,
  Gift
} from 'lucide-react';

const PaymentModal = ({ isOpen, onClose, chatbot, onPaymentSuccess }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [polling, setPolling] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1: determine flow, 2: final payment, 3: plan selection
  const [finalPaymentCompleted, setFinalPaymentCompleted] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Subscription plans
  const plans = [
    {
      id: 'prod_SsIDOZF9Qv3XOK',
      name: 'Starter',
      type: 'monthly',
      price: 25,
      period: 'month',
      features: [
        'Up to 1,000 messages/month',
        'Basic AI training',
        'Email support',
        'Standard integrations',
        'Basic analytics'
      ],
      color: 'from-blue-400 to-cyan-500',
      icon: Users,
      popular: false
    },
    {
      id: 'prod_SsIGZ5PSk9nisL',
      name: 'Starter',
      type: 'yearly',
      price: 270,
      period: 'year',
      monthlyEquivalent: 22.50,
      originalPrice: 300,
      features: [
        'Up to 1,000 messages/month',
        'Basic AI training',
        'Email support',
        'Standard integrations',
        'Basic analytics',
        '2 months FREE'
      ],
      color: 'from-blue-400 to-cyan-500',
      icon: Users,
      popular: false
    },
    {
      id: 'prod_SsIKta0SdwAFRi',
      name: 'Professional',
      type: 'monthly',
      price: 30,
      period: 'month',
      features: [
        'Up to 1,500 messages/month',
        'Advanced AI training',
        '24/7 priority support',
        'WhatsApp integration',
        'Advanced analytics'
      ],
      color: 'from-violet-400 to-purple-500',
      icon: Zap,
      popular: true
    },
    {
      id: 'prod_SsILklIlBnHeLS',
      name: 'Professional',
      type: 'yearly',
      price: 324,
      period: 'year',
      monthlyEquivalent: 27,
      originalPrice: 360,
      features: [
        'Up to 1,500 messages/month',
        'Advanced AI training',
        '24/7 priority support',
        'WhatsApp integration',
        'Advanced analytics',
        '2 months FREE'
      ],
      color: 'from-violet-400 to-purple-500',
      icon: Zap,
      popular: true
    },
    {
      id: 'prod_SsIOBWZDnZdzrn',
      name: 'Enterprise',
      type: 'monthly',
      price: 60,
      period: 'month',
      features: [
        'Up to 3,000 messages/month',
        'Advanced AI training',
        '24/7 priority support',
        'WhatsApp, Messenger & Instagram',
        'Advanced analytics',
        'Custom branding',
        'Dedicated account manager'
      ],
      color: 'from-amber-400 to-orange-500',
      icon: Crown,
      popular: false
    },
    {
      id: 'prod_SsIPPwir2G070V',
      name: 'Enterprise',
      type: 'yearly',
      price: 648,
      period: 'year',
      monthlyEquivalent: 54,
      originalPrice: 720,
      features: [
        'Up to 3,000 messages/month',
        'Advanced AI training',
        '24/7 priority support',
        'WhatsApp, Messenger & Instagram',
        'Advanced analytics',
        'Custom branding',
        'Dedicated account manager',
        '2 months FREE'
      ],
      color: 'from-amber-400 to-orange-500',
      icon: Crown,
      popular: false
    }
  ];

  // Check for payment success on mount and determine flow
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const returnedSessionId = urlParams.get('session_id');
    const finalPaymentSuccess = urlParams.get('final_payment_success');
    const planSelectionSuccess = urlParams.get('plan_selection_success');
    
    console.log('PaymentModal mounted, URL params:', { returnedSessionId, finalPaymentSuccess, planSelectionSuccess });
    
    if (returnedSessionId && finalPaymentSuccess === 'true') {
      console.log('Detected return from final payment, checking status...');
      setSessionId(returnedSessionId);
      setPolling(true);
      checkFinalPaymentStatus(returnedSessionId);
      
      // Clean up URL
      const newUrl = window.location.pathname + window.location.search
        .replace(/[?&]final_payment_success=true/, '')
        .replace(/[?&]session_id=[^&]*/, '')
        .replace(/^&/, '?');
      window.history.replaceState({}, document.title, newUrl || window.location.pathname);
    } else if (returnedSessionId && planSelectionSuccess === 'true') {
      console.log('Detected return from plan selection, verifying...');
      setSessionId(returnedSessionId);
      setPolling(true);
      checkPlanSelectionStatus(returnedSessionId);
      
      // Clean up URL
      const newUrl = window.location.pathname + window.location.search
        .replace(/[?&]plan_selection_success=true/, '')
        .replace(/[?&]plan_id=[^&]*/, '')
        .replace(/[?&]session_id=[^&]*/, '')
        .replace(/^&/, '?');
      window.history.replaceState({}, document.title, newUrl || window.location.pathname);
    } else {
      // Determine which flow to show
      checkPaymentFlowStatus();
    }
  }, []);

  const checkPaymentFlowStatus = async () => {
    if (!chatbot?.id) return;
    
    try {
      // Check what phase the chatbot is in
      console.log('Checking payment flow status for chatbot:', chatbot);
      
      if (chatbot.status === 'pending' && !chatbot.deposit_paid) {
        // Initial deposit flow ($200)
        setCurrentStep(1);
        console.log('Chatbot needs initial deposit payment');
      } else if (chatbot.status === 'final_payment' || (chatbot.deposit_paid && !chatbot.final_payment_paid)) {
        // Final payment flow ($400)
        console.log('Checking final payment status...');
        const response = await paymentAPI.getFinalPaymentStatus();
        
        if (response.data.final_payment_completed) {
          setFinalPaymentCompleted(true);
          setCurrentStep(3); // Plan selection
        } else {
          setCurrentStep(2); // Final payment
        }
      } else if (chatbot.status === 'deploy' || chatbot.final_payment_paid) {
        // Already completed
        setPaymentStatus('completed');
        onClose();
      } else {
        // Fallback to deposit flow
        setCurrentStep(1);
      }
    } catch (error) {
      console.warn('Failed to check payment flow status:', error);
      // Default to deposit flow
      setCurrentStep(1);
    }
  };

  const checkFinalPaymentStatus = async (sessionIdToCheck) => {
    try {
      const response = await paymentAPI.getPaymentStatus(sessionIdToCheck);
      
      if (response.data.payment_status === 'paid') {
        setPaymentStatus('final_payment_success');
        setFinalPaymentCompleted(true);
        setPolling(false);
        setCurrentStep(3); // Move to plan selection
        
        toast({
          title: "Final Payment Successful!",
          description: "Your $400 final payment has been processed. Please select your subscription plan.",
        });
      } else {
        // Continue polling
        setTimeout(() => checkFinalPaymentStatus(sessionIdToCheck), 2000);
      }
    } catch (error) {
      console.error('Error checking final payment status:', error);
      setPolling(false);
    }
  };

  const checkPlanSelectionStatus = async (sessionIdToCheck) => {
    try {
      const response = await paymentAPI.getPaymentStatus(sessionIdToCheck);
      
      if (response.data.payment_status === 'paid') {
        setPaymentStatus('plan_selection_success');
        setPolling(false);
        
        toast({
          title: "Subscription Activated!",
          description: "Your subscription plan has been activated. Your chatbot is now ready for deployment.",
        });
        
        if (onPaymentSuccess) {
          onPaymentSuccess(chatbot?.id);
        }
        
        setTimeout(() => onClose(), 2000);
      } else {
        // Continue polling
        setTimeout(() => checkPlanSelectionStatus(sessionIdToCheck), 2000);
      }
    } catch (error) {
      console.error('Error checking plan selection status:', error);
      setPolling(false);
    }
  };

  const handleDepositPayment = async () => {
    if (!chatbot?.id) {
      toast({
        title: "Error",
        description: "No chatbot selected for payment",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      
      const response = await paymentAPI.createDepositCheckoutSession(chatbot.id);
      
      if (response.data.checkout_url) {
        setSessionId(response.data.session_id);
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      const errorMessage = handleAPIError(error, 'Failed to initiate payment');
      toast({
        title: "Payment failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFinalPayment = async () => {
    try {
      setLoading(true);
      
      const response = await paymentAPI.createFinalPaymentCheckoutSession(chatbot.id);
      
      // Handle the case where final payment is already completed
      if (response.data && response.data.redirect_to_plan_selection) {
        toast({
          title: "Final Payment Already Completed ✅",
          description: response.data.message || "You can now select your subscription plan.",
        });
        setCurrentStep(3);
        setFinalPaymentCompleted(true);
        setLoading(false);
        return;
      }
      
      // Normal case: redirect to Stripe checkout
      if (response.data && response.data.checkout_url) {
        setSessionId(response.data.session_id);
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      const errorMessage = handleAPIError(error);
      toast({
        title: "Payment Failed",
        description: errorMessage,
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  const handlePlanSelection = async () => {
    if (!selectedPlan) {
      toast({
        title: "No Plan Selected",
        description: "Please select a subscription plan to continue",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await paymentAPI.createPlanSelectionCheckoutSession(chatbot.id, selectedPlan.id);
      if (response.data && response.data.checkout_url) {
        setSessionId(response.data.session_id);
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      const errorMessage = handleAPIError(error);
      toast({
        title: "Plan Selection Failed",
        description: errorMessage,
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-6xl max-h-[90vh] overflow-y-auto bg-gray-900 border-violet-500/20">
        <CardHeader className="border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl text-white flex items-center">
                <CreditCard className="w-8 h-8 mr-3 text-violet-400" />
                {currentStep === 1 && 'Activate Your Chatbot'}
                {currentStep === 2 && 'Final Payment'}
                {currentStep === 3 && 'Choose Your Plan'}
              </CardTitle>
              <CardDescription className="text-gray-400 mt-2">
                {currentStep === 1 && 'Complete your initial deposit to activate your chatbot'}
                {currentStep === 2 && 'Complete your final payment to unlock plan selection'}
                {currentStep === 3 && 'Select your subscription plan to finalize setup'}
              </CardDescription>
            </div>
            <Button variant="ghost" onClick={onClose} className="text-gray-400 hover:text-white">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-6">
          {polling ? (
            // Payment processing state
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
              </div>
              <p className="text-gray-300">
                Verifying your payment with Stripe...
              </p>
            </div>
          ) : paymentStatus === 'final_payment_success' || paymentStatus === 'plan_selection_success' || paymentStatus === 'completed' ? (
            // Success state
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto">
                <Check className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-green-400 text-lg font-semibold">
                {paymentStatus === 'final_payment_success' && 'Final Payment Successful!'}
                {paymentStatus === 'plan_selection_success' && 'Subscription Activated!'}
                {paymentStatus === 'completed' && 'Setup Complete!'}
              </h3>
              <p className="text-gray-300">
                {paymentStatus === 'final_payment_success' && 'You can now select your subscription plan.'}
                {paymentStatus === 'plan_selection_success' && 'Your chatbot is ready for deployment.'}
                {paymentStatus === 'completed' && 'Your chatbot setup is complete.'}
              </p>
            </div>
          ) : currentStep === 1 ? (
            // Initial deposit flow ($200)
            <>
              <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4 mb-4">
                <div className="flex items-start space-x-3">
                  <MessageSquare className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-green-300 font-medium mb-1">Don't worry - this is just the activation step!</h4>
                    <p className="text-green-200 text-sm">
                      After your deposit, we'll discuss all your requirements and customize everything perfectly.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between text-white">
                  <span>Deposit Amount:</span>
                  <span className="text-2xl font-bold text-violet-400">$200.00</span>
                </div>
                
                <Button
                  onClick={handleDepositPayment}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white py-4 text-lg font-semibold"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Pay $200 Deposit
                    </>
                  )}
                </Button>
              </div>
            </>
          ) : currentStep === 2 ? (
            // Final payment flow ($400)
            <>
              <div className="bg-violet-900/20 border border-violet-500/30 rounded-lg p-4 mb-4">
                <p className="text-violet-300 text-sm">
                  <Gift className="w-4 h-4 inline mr-1" />
                  Your chatbot is ready for testing! After completing the final payment, you'll be able to select your subscription plan.
                </p>
              </div>

              <div className="space-y-4">
                <div className="text-white">
                  <p className="mb-2">Setup fee breakdown:</p>
                  <div className="space-y-1 text-sm text-gray-300">
                    <div className="flex justify-between">
                      <span>Initial deposit (paid):</span>
                      <span className="text-green-400">$200</span>
                    </div>
                    <div className="flex justify-between font-medium">
                      <span>Remaining amount:</span>
                      <span className="text-violet-400">$400</span>
                    </div>
                    <div className="border-t border-gray-600 pt-1 mt-2 flex justify-between font-semibold">
                      <span>Total setup fee:</span>
                      <span>$600</span>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleFinalPayment}
                  disabled={loading}
                  className="w-full bg-violet-600 hover:bg-violet-700 py-4 text-lg font-semibold"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Pay Final $400
                    </>
                  )}
                </Button>
              </div>
            </>
          ) : (
            // Plan selection flow
            <>
              <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 mb-6">
                <p className="text-blue-300 text-sm flex items-center">
                  <Gift className="w-4 h-4 mr-2" />
                  🎉 <strong>Special Offer:</strong> Get your first month FREE with any plan! Start your trial today.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                {plans.map((plan) => (
                  <Card 
                    key={plan.id}
                    className={`relative cursor-pointer transition-all duration-300 ${
                      selectedPlan?.id === plan.id 
                        ? 'ring-2 ring-violet-500 bg-violet-900/20 border-violet-500/50' 
                        : 'bg-gray-800/50 border-gray-600 hover:border-violet-500/30'
                    } ${plan.popular ? 'ring-1 ring-purple-500/50' : ''}`}
                    onClick={() => setSelectedPlan(plan)}
                  >
                    {plan.popular && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <Badge className="bg-purple-600 text-white px-3 py-1">
                          <Star className="w-3 h-3 mr-1" />
                          Most Popular
                        </Badge>
                      </div>
                    )}
                    
                    <CardHeader className="pb-4">
                      <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${plan.color} flex items-center justify-center mb-4`}>
                        <plan.icon className="w-6 h-6 text-white" />
                      </div>
                      <CardTitle className="text-white text-lg">{plan.name}</CardTitle>
                      <div className="text-2xl font-bold text-white">
                        ${plan.price}
                        <span className="text-sm font-normal text-gray-400">/{plan.period}</span>
                      </div>
                      {plan.monthlyEquivalent && (
                        <div className="text-sm text-gray-400">
                          ${plan.monthlyEquivalent.toFixed(2)}/month
                        </div>
                      )}
                      <div className="text-center">
                        <Badge className="bg-green-600/20 text-green-300 border-green-500/30">
                          <Gift className="w-3 h-3 mr-1" />
                          First month FREE
                        </Badge>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="pt-0">
                      <ul className="space-y-2">
                        {plan.features.map((feature, index) => (
                          <li key={index} className="flex items-center text-sm text-gray-300">
                            <Check className="w-4 h-4 mr-2 text-green-400 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      
                      {selectedPlan?.id === plan.id && (
                        <div className="mt-4 p-2 bg-violet-600/20 rounded-lg">
                          <p className="text-violet-300 text-xs text-center">
                            ✓ Selected Plan
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Button
                onClick={handlePlanSelection}
                disabled={loading || !selectedPlan}
                className="w-full bg-violet-600 hover:bg-violet-700 py-4 text-lg font-semibold"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Subscribe to {selectedPlan ? selectedPlan.name : 'Selected Plan'}
                  </>
                )}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentModal;